import { useDroppable } from '@dnd-kit/core';

import type { BoardColumn } from '@tasks/contracts';

import { CATEGORY_COLOR_VAR } from '../../lib/types';
import { cn } from '../../lib/cn';
import { BoardVirtualList } from './BoardVirtualList';
import { isWipExceeded } from './boardModel';
import styles from './BoardColumnView.module.css';

export type BoardColumnViewProps = {
  readonly column: BoardColumn;
  readonly onOpenIssue: (key: string) => void;
  readonly isTransitionAllowed: (toStatusId: string) => boolean;
  readonly showLaneHeaders?: boolean;
  readonly onLoadMore?: () => void;
};

/** Колонка: имя, категория полосой, счётчик `N` или `N/WIP` (docs/06 §3.1). */
export function BoardColumnView({
  column,
  onOpenIssue,
  isTransitionAllowed,
  showLaneHeaders = false,
  onLoadMore,
}: BoardColumnViewProps) {
  const { setNodeRef, isOver } = useDroppable({
    id: `column:${column.status.id}`,
    data: { statusId: column.status.id },
  });
  const isAllowed = isTransitionAllowed(column.status.id);
  const isExceeded = isWipExceeded(column);
  const allIssues = column.lanes.flatMap((lane) => lane.issues);

  return (
    <section
      ref={setNodeRef}
      className={cn(
        styles['column'],
        isOver && isAllowed && styles['overAllowed'],
        isOver && !isAllowed && styles['overDenied'],
      )}
      aria-label={`Колонка ${column.status.name}`}
    >
      <header className={styles['header']}>
        <span aria-hidden className={styles['stripe']} style={{ background: CATEGORY_COLOR_VAR[column.status.category] }} />
        <h3 className={styles['name']}>{column.status.name}</h3>
        <span
          className={cn(styles['count'], isExceeded && styles['countExceeded'])}
          title={isExceeded ? `Превышен WIP-лимит: ${column.wipLimit}` : undefined}
        >
          {column.wipLimit !== null && column.wipLimit > 0
            ? `${column.issueCount}/${column.wipLimit}`
            : column.issueCount}
        </span>
      </header>

      {allIssues.length === 0 ? (
        <p className={styles['empty']}>Нет задач</p>
      ) : showLaneHeaders ? (
        <div className={styles['lanes']}>
          {column.lanes.map((lane) => (
            <div key={lane.key} className={styles['lane']}>
              <div className={styles['laneHeader']}>
                <span className={styles['laneTitle']}>{lane.title}</span>
                <span className={styles['laneCount']}>{lane.count}</span>
              </div>
              {lane.issues.length === 0 ? (
                <p className={styles['laneEmpty']}>Пусто</p>
              ) : (
                <BoardVirtualList
                  issues={lane.issues}
                  onOpenIssue={onOpenIssue}
                  hasMore={lane.hasMore}
                  isLoadingMore={false}
                  onLoadMore={onLoadMore ?? (() => undefined)}
                />
              )}
            </div>
          ))}
        </div>
      ) : (
        <BoardVirtualList
          issues={allIssues}
          onOpenIssue={onOpenIssue}
          hasMore={column.lanes.some((lane) => lane.hasMore)}
          isLoadingMore={false}
          onLoadMore={onLoadMore ?? (() => undefined)}
        />
      )}
    </section>
  );
}
